import { useEffect, useRef, useState } from 'react';
import {
  buildInitialItem,
  getArrayItemEntries,
  getDescriptionLabel,
  getMapFieldEntries,
  isArrayView,
  isMapView,
  type NavigationItem,
  type PeekFieldType,
} from './field-peek-panel.domain';

export interface FieldPeekPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldName: string;
  value: unknown;
  fieldType: PeekFieldType;
}

const COPY_FEEDBACK_MS = 2000;
const FIELD_PEEK_VIEW_KEY = 'firestore-field-peek-view';

function getStoredViewAsJson(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(FIELD_PEEK_VIEW_KEY) === 'json';
  } catch {
    return false;
  }
}

function setStoredViewAsJson(viewAsJson: boolean) {
  try {
    localStorage.setItem(FIELD_PEEK_VIEW_KEY, viewAsJson ? 'json' : 'structured');
  } catch {
    // ignore storage errors
  }
}

export function useFieldPeekPanel({
  open,
  onOpenChange,
  fieldName,
  value,
  fieldType,
}: FieldPeekPanelProps) {
  const [viewAsJson, setViewAsJsonState] = useState(getStoredViewAsJson);
  const [copied, setCopied] = useState(false);
  const [navigationStack, setNavigationStack] = useState<NavigationItem[]>([
    buildInitialItem(value, fieldName, fieldType),
  ]);

  useEffect(() => {
    queueMicrotask(() => setNavigationStack([buildInitialItem(value, fieldName, fieldType)]));
  }, [value, fieldName, fieldType]);

  const currentItem = navigationStack[navigationStack.length - 1];
  const canGoBack = navigationStack.length > 1;
  const descriptionLabel = getDescriptionLabel(currentItem);
  const showArrayView = isArrayView(currentItem);
  const showMapView = isMapView(currentItem);
  const arrayEntries = showArrayView
    ? getArrayItemEntries(currentItem.value, currentItem.path)
    : [];
  const mapEntries = showMapView ? getMapFieldEntries(currentItem.value, currentItem.path) : [];

  const handleCopyJson = async () => {
    try {
      const jsonString = JSON.stringify(currentItem.value, null, 2);
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePeek = (nestedValue: unknown, nestedFieldType: PeekFieldType, path: string) => {
    setNavigationStack((prev) => [
      ...prev,
      { value: nestedValue, fieldName: path, fieldType: nestedFieldType, path },
    ]);
  };

  const handleGoBack = () => {
    setNavigationStack((prev) => prev.slice(0, -1));
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setNavigationStack([buildInitialItem(value, fieldName, fieldType)]);
    }
    onOpenChange(isOpen);
  };

  const canGoBackRef = useRef(canGoBack);
  const handleGoBackRef = useRef(handleGoBack);
  const handleOpenChangeRef = useRef(handleOpenChange);
  canGoBackRef.current = canGoBack;
  handleGoBackRef.current = handleGoBack;
  handleOpenChangeRef.current = handleOpenChange;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (canGoBackRef.current) {
        handleGoBackRef.current();
      } else {
        handleOpenChangeRef.current(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const setViewStructured = () => {
    setViewAsJsonState(false);
    setStoredViewAsJson(false);
  };
  const setViewJson = () => {
    setViewAsJsonState(true);
    setStoredViewAsJson(true);
  };

  return {
    open,
    onOpenChange: handleOpenChange,
    currentItem,
    canGoBack,
    descriptionLabel,
    viewAsJson,
    setViewStructured,
    setViewJson,
    copied,
    handleCopyJson,
    handlePeek,
    handleGoBack,
    showArrayView,
    showMapView,
    arrayEntries,
    mapEntries,
  };
}
